import { AttributeOptionDetails } from "interfaces/products";
import { createContext, useContext } from "react";

import {
    AccountOverviewList,
    PDPFavoriteChoice,
    PDPFavoriteItem,
    RulesetFavoriteChoice,
    RulesetFavoriteItem,
} from "./Favorites/interfaces";

export type UserStatus = "registered" | "lazy" | "unregistered";

type PartiallyConfiguredFavorite = {
    favoritesLists: AccountOverviewList[];
    setFavoritesList?: (lists: AccountOverviewList[]) => void;
    userStatus: UserStatus;
    setUserStatus?: (status: UserStatus) => void;
};

/**
 * This is intended at least for use with the favorites modal, so at some point this might get
 * the form data interface from ProductCodeConfigurator
 */
export type PartiallyConfiguredProduct = PartiallyConfiguredFavorite & {
    details: AttributeOptionDetails | null;
    favoriteItem: PDPFavoriteItem | null;
    favoriteItemPieces: {
        image: string[];
        page: number;
        choices: Omit<PDPFavoriteChoice, "id">[];
        title: string;
    } | null;
    productCodes: string[];
    pageTitle?: {
        pageTitle: string;
        configString: string;
    };
};

const productContext = createContext<PartiallyConfiguredProduct | null>(null);

export type PartiallyConfiguredRuleset = PartiallyConfiguredFavorite & {
    favoriteItem: RulesetFavoriteItem | null;
    favoriteItemPieces: {
        image: string[];
        page: number;
        choices: Omit<RulesetFavoriteChoice, "id">[];
        title: string;
    } | null;
};

const rulesetContext = createContext<PartiallyConfiguredRuleset | null>(null);

/**
 * We wrap productContext.Provider so that:
 * 1 - we can make the props required
 * 2 - we can pass individual context values as props instead of passing them all on a `value` prop
 */
export function ProductProvider({
    children,
    ...value
}: PartiallyConfiguredProduct & { children?: any }) {
    return (
        <productContext.Provider value={value}>
            {children}
        </productContext.Provider>
    );
}

export function RulesetProvider({
    children,
    ...value
}: PartiallyConfiguredRuleset & { children?: any }) {
    return (
        <rulesetContext.Provider value={value}>
            {children}
        </rulesetContext.Provider>
    );
}

/**
 * Since ProductProvider requires the right values be passed in, the only way there's no data is if
 * someone didn't wrap something in ProductProvider that needs to be. To ensure that users of the hook
 * can rely on the data being there (instead of potentially being null) we throw an error in that case
 */
export function usePartiallyConfiguredProduct() {
    const data = useContext(productContext);
    if (!data) throw new Error("Please render this inside a ProductProvider");
    return data;
}

export function usePartiallyConfiguredRuleset() {
    const data = useContext(rulesetContext);
    if (!data) throw new Error("Please render this inside a RulesetProvider");
    return data;
}
